import { isDefined, isNotDefined, makeValuesPredicate } from '@sgme/fp';
import { parsePastedValue } from 'components/common-form/builder/form/node/field/Grid/pasting';
import {
  BatchPasteDataContextValue,
  FailedPasteCell,
} from 'components/common-form/contexts/batch/BatchPasteDataContext';
import { TenorFieldData } from 'components/shared/TenorField/TenorField';
import type { SPIntl } from 'contexts/intl/intlContext';
import { isEqual as deepEqual, isEmpty } from 'lodash-es';
import { CellId } from 'models/batch/navigation';
import type { FieldMetadata, FieldType, LockablePercentFieldData } from 'models/forms/fields';
import { AvailableItems } from 'models/forms/fields/AvailableItemsField';
import { ISalesCounterpart } from 'models/ISalesCounterpart';
import { Underlying } from 'models/underlying';
import type { ProductGraph } from 'state/product-description/model';
import { GreeksTable } from 'state/quotes/details/details.model';
import { roundNumberToPrecision } from 'utils/core/math';
import { getDateOnlyFromIsoString } from 'utils/date';

export type BatchFormFieldType =
  | string
  | number
  | boolean
  | LockablePercentFieldData
  | readonly Underlying[]
  | ISalesCounterpart
  | TenorFieldData
  | GreeksTable
  | null;
export function getColumnIndex(formIndex: number | undefined) {
  return formIndex === undefined ? 0 : formIndex;
}

export function getRowIndex(blockIndex: number, fieldIndex: number, productDescription: ProductGraph) {
  if (blockIndex === 0) {
    return fieldIndex;
  } else if (fieldIndex === undefined) {
    return 0;
  } else {
    return totalItemsInChildren(productDescription, blockIndex) + fieldIndex;
  }
}

export function getTabIndex(blockIndex?: number, fieldIndex?: number, formIndex?: number) {
  return Number(`${(blockIndex ?? 0) + 1}${(fieldIndex ?? 0) + 1}${(formIndex ?? 0) + 1}`);
}

export const isFieldWithInactiveMode = makeValuesPredicate<FieldType>([
  'NumberField',
  'PercentField',
  'LockablePercentField',
  'AmountField',
  'ClientListField',
  'CustomizableTenorField',
  'UnderlyingsField',
  'MultipleUnderlyingsField',
  'DateField',
]);

export function getIsCellInactive(isEditing: boolean, isSelected: boolean, isFieldWithInactiveMode: boolean) {
  if (!isFieldWithInactiveMode) {
    return false;
  } else if (isEditing && isSelected) {
    return false;
  } else {
    return true;
  }
}

export function switchRadioButtons(
  availableItems: AvailableItems,
  value: string,
  setValue: (value: Readonly<Nullable<string>>) => void,
  index?: number,
) {
  const items = availableItems.map(x => x.value);
  const currentIndex = items.indexOf(value);
  if (index) {
    if (items[currentIndex] !== items[index]) {
      setValue(items[index]);
    }
    return;
  }
  const nextIndex = (currentIndex + 1) % items.length;
  setValue(items[nextIndex]);
}

function totalItemsInChildren(productDescription: ProductGraph, n: number): number {
  let totalItems = 0;

  for (let i = 0; i < Math.min(productDescription.length, n); i++) {
    totalItems += productDescription[i].children?.length ?? 0;
  }

  return totalItems;
}

export function isValueValid(
  value: BatchFormFieldType,
  metadata: FieldMetadata,
  intlMessageFormatter: SPIntl['formatMessage'],
): BatchFormFieldType | undefined {
  switch (metadata.type) {
    case 'ListField':
    case 'CurrencyListField': {
      const { availableItems } = metadata;

      return availableItems.find(item => {
        const translatedLabel = intlMessageFormatter({
          id: `form.SolvingMode.${item.translationKey}` as any,
        });
        return item.value === value || translatedLabel === value;
      })?.value;
    }
    default:
      return value;
  }
}

export function formatValue<T = string>(value: BatchFormFieldType | undefined, type: string): T | undefined {
  if (isNotDefined(value)) {
    return undefined;
  }
  switch (type) {
    case 'ToggleField':
      return ((value === true) as T) || ((value === 'true') as T);
    case 'DateField':
      return getDateOnlyFromIsoString(value as string) as unknown as T;
    default:
      return value as T;
  }
}

export function buildSingleCellId(rowIndex: number, columnIndex: number): CellId {
  return `${rowIndex}-${columnIndex}`;
}

export function isLockFieldValue(value?: BatchFormFieldType): value is LockablePercentFieldData {
  if (!isDefined(value)) {
    return false;
  }
  return value.hasOwnProperty('value') && value.hasOwnProperty('customized');
}

export function updateFieldValueOnDrag(
  fieldType: string,
  fieldValue: Readonly<BatchFormFieldType | undefined>,
  draggedValue: BatchFormFieldType | undefined,
  setValue: (value: Readonly<BatchFormFieldType | undefined>) => void,
): void {
  const draggedValueIsLockField = isDefined(draggedValue) && isLockFieldValue(draggedValue);
  const formattedCurrentValue = formatValue<BatchFormFieldType>(fieldValue, fieldType);

  const formattedDraggedValue = draggedValueIsLockField
    ? formatValue<LockablePercentFieldData>(draggedValue, fieldType)
    : formatValue<BatchFormFieldType>(draggedValue, fieldType);

  if (areCurrentAndDraggedValueEqual(fieldType, formattedCurrentValue, formattedDraggedValue)) {
    return;
  }
  if (fieldType === 'LockablePercentField') {
    // Use case : Dragging values over LockablePercentField
    updateLockablePercentFieldValueOnDrag(
      fieldValue,
      formattedDraggedValue as LockablePercentFieldData | undefined,
      draggedValueIsLockField,
      setValue,
    );
    return;
  }

  if (fieldType === 'PercentField') {
    // Use case : Dragging values over Percent fields from LockablePercent fields
    updatePercentFieldValueOnDrag(
      formattedCurrentValue as string | undefined,
      formattedDraggedValue as string | LockablePercentFieldData | undefined,
      setValue,
    );
    return;
  }

  setValue(formattedDraggedValue);
}

function updateLockablePercentFieldValueOnDrag(
  fieldValue: Readonly<BatchFormFieldType | undefined>,
  draggedValue: string | LockablePercentFieldData | undefined,
  draggedValueIsLockField: boolean,
  setValue: (value: Readonly<BatchFormFieldType | undefined>) => void,
) {
  const currentLockFieldValue = fieldValue as LockablePercentFieldData | undefined;
  if (deepEqual(currentLockFieldValue?.value, draggedValue)) {
    return;
  }

  if (!isDefined(draggedValue)) {
    setValue(null);
    return;
  }
  const newLockFieldValue: LockablePercentFieldData = draggedValueIsLockField
    ? (draggedValue as LockablePercentFieldData)
    : { value: parseFloat(draggedValue as string), customized: true };
  setValue(newLockFieldValue);
}

function updatePercentFieldValueOnDrag(
  currentValue: string | undefined,
  draggedValue: string | LockablePercentFieldData | undefined,
  setValue: (value: Readonly<BatchFormFieldType | undefined>) => void,
) {
  const draggedPercentValue = isLockFieldValue(draggedValue) ? draggedValue?.value : draggedValue;
  if (isNotDefined(draggedPercentValue) || deepEqual(currentValue, draggedPercentValue)) {
    return;
  }
  setValue(draggedPercentValue);
}

export function updateFieldValueOnGridDataPasted(
  metadata: FieldMetadata,
  value: Readonly<BatchFormFieldType | undefined>,
  cellId: string,
  { failedPasteCells, setPastedGridData, setFailedPasteCells, pastedGridData: pastedData }: BatchPasteDataContextValue,
  setValue: (value: Readonly<BatchFormFieldType | undefined>) => void,
  intlMessageFormatter: SPIntl['formatMessage'],
): void {
  if (isNotDefined(pastedData)) {
    return;
  }

  const pasteDataCellIds = Object.keys(pastedData);
  if (pasteDataCellIds.length === 0 || pasteDataCellIds[0] !== cellId) {
    return;
  }

  const rawPastedValue = pastedData[cellId];
  if (isEmpty(rawPastedValue)) {
    return;
  }

  const updatedPasteData: Record<string, string> = Object.assign(
    {},
    ...Object.entries(pastedData)
      .filter(([id, _]) => id !== cellId)
      .map(([id, cellData]) => ({ [id]: cellData })),
  );
  const parsedValue = parsePastedValue(rawPastedValue, metadata, 'yyyy-MM-dd');
  const validValue = isDefined(parsedValue) ? isValueValid(parsedValue, metadata, intlMessageFormatter) : undefined;

  if (isNotDefined(validValue)) {
    setPastedGridData?.(updatedPasteData);
    const newFailedPasteCells: FailedPasteCell[] = [
      ...(failedPasteCells ?? []),
      { cellId, pastedValue: rawPastedValue, fieldId: metadata.id },
    ];
    setFailedPasteCells?.(newFailedPasteCells);
    return;
  }

  if (validValue === value || metadata.isReadOnly) {
    setPastedGridData?.(updatedPasteData);
    return;
  }

  const formattedCurrentValue = formatValue(value, metadata.type);
  const formattedPastedValue = formatValue(validValue, metadata.type);

  if (isNotDefined(formattedPastedValue)) {
    return;
  }

  if (metadata.type === 'CustomizableTenorField') {
    const newTenor: TenorFieldData = {
      ...(value as TenorFieldData),
      currentValue: {
        ...(value as TenorFieldData).currentValue,
        value: roundNumberToPrecision(parseFloat(formattedPastedValue), 0),
      },
    };

    if (!deepEqual(formattedCurrentValue, newTenor)) {
      setValue(newTenor);
    }
  } else if (!deepEqual(formattedCurrentValue, formattedPastedValue)) {
    setValue(formattedPastedValue);
  }

  setPastedGridData?.(updatedPasteData);
}

function areCurrentAndDraggedValueEqual(
  fieldType: string,
  currentValue: BatchFormFieldType | undefined,
  draggedValue: BatchFormFieldType | undefined,
) {
  if (fieldType === 'MultipleUnderlyingsField' || fieldType === 'UnderlyingsField') {
    const currentUnderlyingIds = (currentValue as Underlying[])?.map(udl => udl.id) ?? [];
    const draggedUnderlyingIds = (draggedValue as Underlying[])?.map(udl => udl.id) ?? [];

    return deepEqual(currentUnderlyingIds, draggedUnderlyingIds);
  }
  return deepEqual(currentValue, draggedValue);
}
